import React, { useEffect, useState } from 'react';

import { ArrowLeft, ChevronRight } from 'lucide-react';

import { MoreFiltersIcon } from '#/assets/svgs';
import {
    Button,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
    TooltipProvider,
} from '#/components/ui';
import { CITY_KEY, cn, FILTER_TYPE_DYNAMIC, STATE_KEY } from '#/utils';
import { FilterRenderer } from '.';
import { StateFilter, CityFilter } from './GeoFilters';

const MoreFiltersDropdown = ({ moreFilters, queryParams, handleFilterChange, setQueryParams, geoData }) => {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(null);

    useEffect(() => {
        if (Object.keys(queryParams).length === 0) {
            setActive(null);
        }
    }, [queryParams]);

    const countries = queryParams.country || [];
    const states = queryParams.state || [];
    const handleFilterChangeWrapper = name => value => handleFilterChange(name, value);

    const handleActive = value => () => {
        setActive(value);
    };
    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            {moreFilters?.length > 0 && (
                <DropdownMenuTrigger asChild>
                    <Button variant='outline' className='bg-white text-sm font-medium text-gray700'>
                        <MoreFiltersIcon /> More Filters
                    </Button>
                </DropdownMenuTrigger>
            )}

            <DropdownMenuContent
                align='end'
                className={cn(!active ? 'w-48' : 'w-60', 'bg-white rounded-lg shadow-lg max-h-[450px] overflow-auto')}
            >
                {!active ? (
                    <div className='flex flex-col gap-2'>
                        {moreFilters?.map(item => {
                            const isState = item.name === STATE_KEY;
                            const isCity = item.name === CITY_KEY;

                            const stateDataLoaded = !!Object.values(geoData?.statesByCountry)?.flat()?.length;
                            const cityDataLoaded = !!Object.values(geoData?.citiesByState)?.flat()?.length;

                            const isDisabled = (isState && !stateDataLoaded) || (isCity && !cityDataLoaded);

                            return (
                                <TooltipProvider delayDuration={300} key={item.name}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div>
                                                <Button
                                                    variant='ghost'
                                                    disabled={isDisabled}
                                                    className={cn(
                                                        'justify-between w-full text-sm font-normal hover:bg-gray100',
                                                        isDisabled ? 'text-gray400 cursor-not-allowed' : 'text-gray700'
                                                    )}
                                                    onClick={!isDisabled ? handleActive(item) : undefined}
                                                >
                                                    {item.label}
                                                    <ChevronRight
                                                        className={cn(isDisabled ? 'text-gray400' : 'text-gray500')}
                                                    />
                                                </Button>
                                            </div>
                                        </TooltipTrigger>

                                        {isDisabled && (
                                            <TooltipContent
                                                side='top'
                                                className='bg-white text-textPrimary text-xs px-2 py-2 border rounded shadow'
                                            >
                                                {isState && 'Please select a country first to enable states.'}
                                                {isCity && 'Please select a state first to enable cities.'}
                                            </TooltipContent>
                                        )}
                                    </Tooltip>
                                </TooltipProvider>
                            );
                        })}
                    </div>
                ) : (
                    <div className='flex flex-col gap-4'>
                        <div
                            className='flex px-2 pt-2 items-center gap-2 font-normal cursor-pointer text-sm text-gray800 hover:text-gray700 mb-2'
                            onClick={handleActive(null)}
                        >
                            <ArrowLeft className='w-4 h-4 ' />
                            {active.label}
                        </div>
                        {active.name === STATE_KEY && (
                            <StateFilter
                                countryCodes={countries}
                                selected={states}
                                onChange={handleFilterChangeWrapper(STATE_KEY)}
                                options={Object.values(geoData?.statesByCountry)?.flat()}
                                disabled={!Object.values(geoData?.statesByCountry)?.flat()?.length}
                            />
                        )}
                        {active.name === CITY_KEY && countries?.length > 0 && (
                            <CityFilter
                                countryCode={countries[0]}
                                stateCodes={states}
                                selected={queryParams?.city || []}
                                onChange={handleFilterChangeWrapper(CITY_KEY)}
                                options={Object.values(geoData?.citiesByState)?.flat()}
                                geoData={geoData}
                                disabled={!Object.values(geoData?.citiesByState)?.flat()?.length}
                            />
                        )}

                        {active.type !== FILTER_TYPE_DYNAMIC && (
                            <FilterRenderer
                                filter={active}
                                queryParams={queryParams}
                                onChange={handleFilterChangeWrapper(active.name)}
                                setQueryParams={setQueryParams}
                                showActive={true}
                            />
                        )}
                    </div>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export { MoreFiltersDropdown };
